import type { PerfilVeiculo } from '../didatico/perfilVeiculo'
import type { ProporcoesVitima } from '../didatico/perfisVitima'
import type { MapaEstados } from '../didatico/estadoPecas'
import type { IdVidro, EstadoManejoVidro, ProtecoesManejo } from '../didatico/manejoVidros'
import type { NivelDanoVeicular, RegiaoDanoVeicular } from '../didatico/danoVeicular'
import type { EquipamentoEstabilizacao } from '../didatico/estabilizacao'
import { CORES_TRIAGEM, ORDEM_TRIAGEM, type PrioridadeTriagem } from '../didatico/ocupantes'
import type { RegiaoAnatomicaVitima } from './atendimento/tipos'

// A triagem START é canonicamente definida em `didatico/ocupantes.ts` (junto
// com `EstadoClinico`, que Cinemática/Extração continuam usando); o Table
// top só reexporta para quem importa a partir deste barrel.
export { CORES_TRIAGEM, ORDEM_TRIAGEM }
export type { PrioridadeTriagem, RegiaoAnatomicaVitima }

/**
 * Tipos de objeto do Table top.
 *
 * `carro`/`suv` continuam existindo para objetos v1 recém-migrados sem bloco
 * `veiculo` (ver `migracao.ts`); todo objeto novo do tipo veículo já nasce com
 * o bloco preenchido. `bloco` é o bloco de cribbing (mantém o nome do v1); os
 * demais equipamentos de estabilização são tipos próprios.
 */
export const TIPOS_OBJETO_TABLETOP = [
  'carro',
  'suv',
  'moto',
  'caminhao',
  'onibus',
  'caminhaoBombeiros',
  'resgate',
  'bombeiro',
  'bombeiroMedico',
  'arvore',
  'poste',
  'defensa',
  'cone',
  'bloco',
  'vitima',
  'vidro',
  'detritosVidro',
  'calco',
  'cunha',
  'vstrut',
  'fitaCatraca',
  'cobertor',
  'lagrima',
  'prancha',
  'muro',
  'derramamento',
  'caboEletrico',
  'areaUsa',
  'manchaSangue',
  'extintorPQS',
  'lonaFerramentas',
  'mochilaTrauma',
  'fogo',
] as const

export type TipoObjetoTabletop = (typeof TIPOS_OBJETO_TABLETOP)[number]

/** Tipos que sempre carregam o bloco `veiculo`. */
export const TIPOS_VEICULO_TABLETOP: readonly TipoObjetoTabletop[] = ['carro', 'suv']

/** Tipos de equipamento de estabilização inseríveis no Table top. */
export const TIPOS_EQUIPAMENTO_TABLETOP: readonly TipoObjetoTabletop[] = [
  'calco',
  'cunha',
  'vstrut',
  'fitaCatraca',
  'bloco',
]

export type VetorTabletop = readonly [number, number, number]

export interface TransformacaoTabletop {
  readonly posicao: VetorTabletop
  readonly rotacao: VetorTabletop
  readonly escala: VetorTabletop
}

/**
 * Como o veículo parou — a mesma ideia de `ConfiguracaoVeiculo`, mas com o
 * tombamento desdobrado em dois lados: no Table top o aluno decide para qual
 * lado o carro caiu, algo que o módulo de Extração (um só veículo, um só lado
 * fixo) nunca precisou representar.
 */
export type PosicaoFinalVeiculo =
  | 'sobreRodas'
  | 'tombadoEsquerdo'
  | 'tombadoDireito'
  | 'capotado'
  | 'sobreOutroVeiculo'

export const POSICOES_FINAIS_VEICULO: readonly PosicaoFinalVeiculo[] = [
  'sobreRodas',
  'tombadoEsquerdo',
  'tombadoDireito',
  'capotado',
  'sobreOutroVeiculo',
]

export const IDS_PORTAS_TABLETOP = [
  'portaDianteiraEsquerda', 'portaDianteiraDireita',
  'portaTraseiraEsquerda', 'portaTraseiraDireita',
] as const
export type IdPortaTabletop = (typeof IDS_PORTAS_TABLETOP)[number]

export interface DescritorDeformacaoTabletop {
  readonly regiao: RegiaoDanoVeicular
  readonly nivel: NivelDanoVeicular
  readonly intrusaoM: number
  readonly larguraM?: number
  /** Deslocamento ao longo da face: X para frente/traseira/teto; Z para laterais. */
  readonly centroFaceM?: number
  readonly alturaContatoY?: number
  /** Permite amassar cada porta sem substituir o dano da lateral inteira. */
  readonly idPorta?: IdPortaTabletop
  /** Identificador independente de um amassado aplicado diretamente na superfície. */
  readonly idAmassado?: string
  /** Coordenada Z usada para limitar um amassado no teto. */
  readonly centroLongitudinalM?: number
}

export type EstadoCortePecaTabletop = 'aberta' | 'afastada' | 'removida'

export interface DescritorCorteTabletop {
  readonly idPeca: string
  readonly estado: EstadoCortePecaTabletop
  readonly rotulo?: string
}

export interface BlocoVeiculoTabletop {
  readonly idPerfil: PerfilVeiculo['id']
  readonly posicaoFinal: PosicaoFinalVeiculo
  /** portas abertas, coluna removida, capô afastado etc. — mesmo mapa da maquete didática */
  readonly estadosPecas: MapaEstados
  readonly vidros: Readonly<Partial<Record<IdVidro, EstadoManejoVidro>>>
  readonly insulfilm: boolean
  readonly protecoes: ProtecoesManejo
  readonly dano?: Readonly<{ regiao: RegiaoDanoVeicular; nivel: NivelDanoVeicular; intrusaoM?: number }>
  readonly deformacao?: DescritorDeformacaoTabletop | null
  /** Várias regiões independentes. Ausente em cenas antigas: usar `deformacao`. */
  readonly deformacoes?: readonly DescritorDeformacaoTabletop[]
  readonly cortes?: readonly DescritorCorteTabletop[]
  readonly pecaSelecionada?: string | null
}

export type PosturaVitimaSolo =
  | 'emPe'
  | 'sentada'
  | 'decubitoDorsal'
  | 'decubitoVentral'
  | 'decubitoLateral'

export type LocalVitimaTabletop =
  | Readonly<{ tipo: 'veiculo'; idVeiculo: string; idAncora: string }>
  | Readonly<{ tipo: 'solo'; postura: PosturaVitimaSolo }>

export interface BlocoVitimaTabletop {
  readonly idPerfil: ProporcoesVitima['id']
  readonly triagem: PrioridadeTriagem
  readonly colarCervical: boolean
  /** desempate manual dentro da mesma cor de triagem; menor valor sai primeiro */
  readonly prioridadeManual?: number
  readonly local: LocalVitimaTabletop
  /** ficha clínica de atendimento (ver `dominio/tabletop/atendimento`) vinculada a esta vítima pelo autor do cenário */
  readonly fichaAtendimentoId?: string
  /** membro anatômico com hemorragia externa visível (destacado no 3D) */
  readonly regiaoHemorragia?: RegiaoAnatomicaVitima
}

export interface BlocoEquipamentoTabletop {
  readonly tipo: EquipamentoEstabilizacao
  readonly comCunhaAjuste?: boolean
  readonly rotacaoGraus?: number
  /** Vetor da base no solo até o ponto de apoio no veículo [dx, dy, dz] */
  readonly alcance?: readonly [number, number, number]
}

/** Tipos de objeto que são riscos operacionais da cena (além de decoração). */
export const TIPOS_RISCO_TABLETOP: readonly TipoObjetoTabletop[] = [
  'detritosVidro',
  'derramamento',
  'caboEletrico',
  'manchaSangue',
  'fogo',
]

/** Materiais que a equipe pode jogar sobre uma mancha de óleo/combustível. */
export const MATERIAIS_ABSORVENTES = ['serragem', 'areia', 'absorvente_industrial'] as const
export type MaterialAbsorvente = (typeof MATERIAIS_ABSORVENTES)[number]

/**
 * Configuração de um risco, preparada pelo instrutor. O estado durante a
 * aula (varrido, contido, concessionária acionada...) fica na sessão — nunca
 * aqui —, para reiniciar voltar ao cenário preparado.
 */
export interface BlocoRiscoTabletop {
  /** derramamento: o produto continua vazando até a equipe conter na fonte */
  readonly vazamentoAtivo?: boolean
  /** derramamento: materiais que a regra desta cena aceita; vazio = efeito a definir pelo instrutor */
  readonly materiaisAceitos?: readonly MaterialAbsorvente[]
}

export interface ObjetoTabletop {
  readonly id: string
  readonly tipo: TipoObjetoTabletop
  readonly nome: string
  readonly transformacao: TransformacaoTabletop
  readonly cor?: string
  readonly bloqueado?: boolean
  readonly oculto?: boolean
  readonly veiculo?: BlocoVeiculoTabletop
  readonly vitima?: BlocoVitimaTabletop
  readonly equipamento?: BlocoEquipamentoTabletop
  readonly risco?: BlocoRiscoTabletop
}

/**
 * Uma informação que a central (COBOM) tem no momento do acionamento. O
 * instrutor decide se ela já é confirmada ou só relato do solicitante, e se
 * entra na mensagem lida à guarnição (`visivel`) — pode revelar depois.
 */
export interface ItemAcionamentoCobom {
  readonly id: string
  readonly texto: string
  readonly confirmado: boolean
  readonly visivel: boolean
}

/**
 * Histórico inicial do acionamento, preparado junto com o cenário. Contém só
 * o que a central sabe — nunca número exato de vítimas, lesões ou riscos que
 * a guarnição deve descobrir na cena. Campo vazio fica vazio: a mensagem diz
 * "a confirmar" em vez de inventar o dado.
 */
export interface AcionamentoCobom {
  readonly natureza: string
  readonly local: string
  /** horário simulado do chamado, livre (ex.: "14h32") */
  readonly horario: string
  readonly recursos: string
  readonly informacoes: readonly ItemAcionamentoCobom[]
}

export interface CenaTabletop {
  readonly versao: 2
  readonly nome: string
  readonly historiaInicial?: string
  readonly objetos: readonly ObjetoTabletop[]
  /** opcional: cenas antigas não têm, e a sessão usa o modelo editável */
  readonly acionamento?: AcionamentoCobom
}

export type NovoObjetoTabletop = Readonly<{
  tipo: TipoObjetoTabletop
  nome?: string
  transformacao?: TransformacaoTabletop
  cor?: string
  veiculo?: BlocoVeiculoTabletop
  vitima?: BlocoVitimaTabletop
  equipamento?: BlocoEquipamentoTabletop
  risco?: BlocoRiscoTabletop
}>

export type AtualizacoesObjetoTabletop = Readonly<{
  nome?: string
  cor?: string
  bloqueado?: boolean
  oculto?: boolean
  transformacao?: Partial<TransformacaoTabletop>
  /** mesclado raso sobre o bloco existente — não recria os campos omitidos */
  veiculo?: Partial<BlocoVeiculoTabletop>
  vitima?: Partial<BlocoVitimaTabletop>
  equipamento?: Partial<BlocoEquipamentoTabletop>
  /** mescla no bloco de risco; cria o bloco se o objeto ainda não tinha */
  risco?: Partial<BlocoRiscoTabletop>
}>

export type GeradorIdTabletop = () => string

